/**
 * API Utilities
 * Helper functions for API endpoints and configuration.
 */

/**
 * Get the base API URL based on environment
 * Handles local development vs production URLs
 */
export const getApiBaseUrl = () => {
  // Use environment variable if available
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback to default URLs based on environment
  if (process.env.NODE_ENV === 'development') {
    // For local development, ensure we use the correct port
    return window.location.hostname === 'localhost' 
      ? 'http://localhost:3001/api'
      : 'http://127.0.0.1:3001/api';
  } else {
    return 'https://api.transtags.com/api';
  }
};

/**
 * Construct a full API URL for a specific endpoint
 * @param {string} endpoint - The API endpoint path (without leading slash)
 * @returns {string} The complete API URL
 */
export const buildApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  
  // Remove any trailing slash from base URL
  const normalizedBaseUrl = baseUrl.endsWith('/') 
    ? baseUrl.slice(0, -1) 
    : baseUrl;
  
  // Remove any leading slash from endpoint
  const normalizedEndpoint = endpoint.startsWith('/') 
    ? endpoint.slice(1) 
    : endpoint;
  
  const fullUrl = `${normalizedBaseUrl}/${normalizedEndpoint}`;
  
  // Log the URL in development mode for debugging
  if (process.env.NODE_ENV === 'development') {
    console.debug(`API URL constructed: ${fullUrl}`);
  }
  
  return fullUrl;
};

/**
 * Create fetch options with proper auth headers
 * @param {Object} options - Base fetch options object
 * @param {string} token - Auth token
 * @returns {Object} Complete fetch options with headers
 */
export const createAuthFetchOptions = (options = {}, token) => {
  // Ensure we have a valid token
  if (!token) {
    console.warn('Attempting to create auth fetch options without a token');
  }
  
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token || ''}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include' // Include cookies if available
  };
};

/**
 * Handle API response with error checking
 * @param {Response} response - Fetch Response object
 * @returns {Promise} Resolved with data or rejected with error
 */
export const handleApiResponse = async (response) => {
  if (!response.ok) {
    // Try to parse error message from JSON response
    try {
      const errorData = await response.json();
      console.error('API error details:', {
        status: response.status,
        url: response.url,
        error: errorData
      });
      throw new Error(errorData.error || `API error: ${response.status}`);
    } catch (e) {
      // If JSON parsing fails, use status text
      console.error('API error (unparseable):', {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      });
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
  }
  
  return response.json();
};

/**
 * Test function to debug API connectivity
 * @returns {Promise} Test results
 */
export const testApiConnectivity = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    const healthUrl = `${baseUrl.replace('/api', '')}/health`;
    
    console.log('Testing API connectivity...');
    console.log('Base URL:', baseUrl);
    console.log('Health URL:', healthUrl);
    console.log('Current hostname:', window.location.hostname);
    console.log('Current protocol:', window.location.protocol);
    
    const response = await fetch(healthUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('Health check response:', {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      type: response.type
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('Health check data:', data);
      return { success: true, data };
    } else {
      return { success: false, error: `HTTP ${response.status}` };
    }
  } catch (error) {
    console.error('API connectivity test failed:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    return { success: false, error: error.message };
  }
};